import { CODE_SYSTEMS, LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction, Timing } from '@medplum/fhirtypes';
import { ada, ADA_DERIVED_FROM, every, itemKind, loincCode, patient, taskCategory } from './builders';

export const OBESITY_ONGOING_MEASUREMENTS_URL = 'https://liza.health/fhir/PlanDefinition/obesity-ongoing-measurements';
export const OBESITY_ONGOING_MEASUREMENTS_VERSION = '1.0.0';
/** Version-pinned canonical, the value an expanded suggestion records as its provenance. */
export const OBESITY_ONGOING_MEASUREMENTS_CANONICAL = `${OBESITY_ONGOING_MEASUREMENTS_URL}|${OBESITY_ONGOING_MEASUREMENTS_VERSION}`;

/** The LOINC codes this protocol tracks, named from the shared `LOINC_CODES`. */
export const OBESITY_ONGOING_CODES = {
  BODY_WEIGHT: LOINC_CODES.BODY_WEIGHT,
  BMI: LOINC_CODES.BMI,
  WAIST_CIRCUMFERENCE: LOINC_CODES.WAIST_CIRCUMFERENCE,
  BLOOD_PRESSURE: LOINC_CODES.BLOOD_PRESSURE_PANEL,
} as const;

/**
 * The obesity codes this protocol answers to. Both systems are listed for the same reason as the
 * other protocols: a diagnosis reaches the chart in whichever system its source used (#10129).
 *
 * `E66` is an ICD-10 *prefix* covering `E66.01`, `E66.9` and the rest of the category. The SNOMED code
 * is matched exactly.
 */
export const OBESITY_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'E66', display: 'Overweight and obesity' },
  { system: CODE_SYSTEMS.SNOMED, code: '414916001', display: 'Obesity' },
] as const;

/** Every action here is an unconditional patient-owned measurement; only code, cadence and words differ. */
const measurement = (
  id: string,
  title: string,
  description: string,
  code: string,
  timing: Timing,
  locus: string,
): PlanDefinitionAction => ({
  id,
  title,
  description,
  code: loincCode(code),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  timingTiming: timing,
  documentation: ada(locus),
});

// --- actions ------------------------------------------------------------------------------------------

const bodyWeight = measurement(
  'body-weight',
  'Body weight',
  'Weight at least yearly.',
  OBESITY_ONGOING_CODES.BODY_WEIGHT,
  every(1, 'a'),
  'Recommendation 8.2a',
);

const bmi = measurement(
  'bmi',
  'Body mass index',
  'BMI at least yearly.',
  OBESITY_ONGOING_CODES.BMI,
  every(1, 'a'),
  'Recommendation 8.2a',
);

/**
 * The ADA raises anthropometrics to at least every three months during active weight-management
 * treatment. That escalation is conditional on being in treatment, which this protocol cannot
 * evaluate, so the unconditional yearly floor is what is encoded.
 */
const waistCircumference = measurement(
  'waist-circumference',
  'Waist circumference',
  'Waist circumference at least yearly.',
  OBESITY_ONGOING_CODES.WAIST_CIRCUMFERENCE,
  every(1, 'a'),
  'Recommendation 8.2b',
);

const bloodPressure = measurement(
  'blood-pressure',
  'Blood pressure',
  'Blood pressure at least every 6 months.',
  OBESITY_ONGOING_CODES.BLOOD_PRESSURE,
  every(6, 'mo'),
  'Recommendation 10.1',
);

// --- the protocol -------------------------------------------------------------------------------------

/**
 * The measurements recommended on an ongoing basis for obesity, for a chart that has the diagnosis.
 * Every action is unconditional and patient-owned, matching the shape of `t2dm-ongoing-measurements`.
 *
 * A scoping caveat worth knowing when reading the citations: the ADA writes these recommendations for
 * people *with diabetes or prediabetes*, and they are the best-specified anthropometric cadences
 * available, so they are cited here for obesity as well. The interval is not in dispute — annual
 * weight, BMI and waist are uncontroversial — but the citation is broader than the source's own
 * population. AACE/TOS names waist circumference as a diagnostic criterion without attaching an
 * interval to it, so it cannot stand in.
 *
 * Glycemic screening is deliberately absent even though the ADA gives it a cadence. Its interval
 * depends on the result of the last screen — three years if normal, yearly if prediabetes — which is
 * a conditional this protocol cannot evaluate, and it belongs to diabetes screening rather than to
 * obesity monitoring. Lipids are absent for the same reason: their cadence is set by whether the
 * patient is on lipid-lowering therapy.
 */
export const obesityOngoingMeasurements: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: OBESITY_ONGOING_MEASUREMENTS_URL,
  version: OBESITY_ONGOING_MEASUREMENTS_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: OBESITY_ONGOING_MEASUREMENTS_URL }],
  name: 'obesity-ongoing-measurements',
  title: 'Obesity — ongoing measurements',
  status: 'active',
  experimental: true,
  type: {
    coding: [
      {
        system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
        code: 'clinical-protocol',
        display: 'Clinical Protocol',
      },
    ],
  },
  date: '2026-09-15',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in obesity, expanded onto a care-plan topic when an obesity Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...OBESITY_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [ADA_DERIVED_FROM],
  action: [
    {
      id: 'ongoing-measurements',
      title: 'Ongoing measurements',
      description: 'Results tracked on an ongoing basis for obesity.',
      action: [bodyWeight, bmi, waistCircumference, bloodPressure],
    },
  ],
};
